import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch, getToken } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const CATEGORIES = ["New", "Active", "Pending", "Resolved", "Closed", "Cancelled"];
const PRIORITIES = ["P1", "P2", "P3", "P4"];

export default function Queue() {
  const [providers, setProviders] = useState([]);
  const [providerKey, setProviderKey] = useState("");
  const [view, setView] = useState(null);
  const [summary, setSummary] = useState(null);
  const [members, setMembers] = useState([]);
  const [filters, setFilters] = useState({ queue: "", status_category: "", assignment: "" });
  const [selected, setSelected] = useState(null);
  const [comments, setComments] = useState([]);
  const [draft, setDraft] = useState({ comment: "", internal: false, priority: "P3",
                                        reason: "", transfer: "", transferReason: "" });
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!getToken()) { navigate("/login"); return; }
    apiFetch("/providers").then((r) => (r.ok ? r.json() : [])).then((list) => {
      setProviders(list);
      if (list.length) setProviderKey((k) => k || list[0].key);
    });
  }, [navigate]);

  const load = useCallback(() => {
    if (!providerKey) return;
    const query = new URLSearchParams({ provider_key: providerKey });
    Object.entries(filters).forEach(([k, v]) => v && query.set(k, v));
    apiFetch(`/requests/queue?${query}`).then(async (r) => {
      const data = await r.json();
      if (!r.ok) { setError(typeof data.detail === "string" ? data.detail : "Queue unavailable");
                   setView(null); return; }
      setError(null); setView(data);
    });
    apiFetch(`/requests/queue/summary?provider_key=${providerKey}`)
      .then((r) => (r.ok ? r.json() : null)).then(setSummary);
    apiFetch(`/requests/queue/members?provider_key=${providerKey}`)
      .then((r) => (r.ok ? r.json() : [])).then(setMembers);
  }, [providerKey, filters]);

  useEffect(() => { load(); }, [load]);

  const openRequest = async (id) => {
    const res = await apiFetch(`/requests/${id}`);
    if (!res.ok) { setStatus("That request is no longer readable"); return; }
    const data = await res.json();
    setSelected(data);
    setDraft((d) => ({ ...d, priority: data.priority }));
    apiFetch(`/comments?parent_type=work_request&parent_id=${id}`)
      .then((r) => (r.ok ? r.json() : [])).then(setComments);
  };

  const post = (path, body, label) =>
    apiFetch(path, { method: "POST", body: JSON.stringify(body) }).then(async (r) => {
      const data = await r.json();
      if (!r.ok) {
        setStatus(`${label} failed — ${typeof data.detail === "string" ? data.detail
                                                                       : JSON.stringify(data.detail)}`);
        return;
      }
      setStatus(`${label} done`);
      load();
      if (selected) openRequest(selected.id);
    });

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold" data-testid="queue-title">Request Queue</h1>
          <p className="text-sm text-muted-foreground">
            Triage, assign and close the work your team owns.
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline" size="sm" data-testid="queue-catalog-link">
            <Link to="/catalog">Raise a request</Link>
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-6 space-y-6">
        {error && <p className="text-sm text-destructive" data-testid="queue-error">{error}</p>}
        {status && <p className="text-sm text-amber-800" data-testid="queue-status">{status}</p>}

        <section className="grid gap-3 md:grid-cols-4" data-testid="queue-filters">
          <div className="space-y-1">
            <label htmlFor="queue-provider" className="text-sm font-medium">Provider</label>
            <select id="queue-provider" data-testid="queue-provider" value={providerKey}
                    className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                    onChange={(e) => { setProviderKey(e.target.value); setSelected(null); }}>
              {providers.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <label htmlFor="queue-queue" className="text-sm font-medium">Queue</label>
            <select id="queue-queue" data-testid="queue-queue" value={filters.queue}
                    className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                    onChange={(e) => setFilters({ ...filters, queue: e.target.value })}>
              <option value="">All queues</option>
              {Object.keys(summary?.queue_load || {}).map((q) => (
                <option key={q} value={q}>{q}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label htmlFor="queue-state" className="text-sm font-medium">Status</label>
            <select id="queue-state" data-testid="queue-state" value={filters.status_category}
                    className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                    onChange={(e) => setFilters({ ...filters, status_category: e.target.value })}>
              <option value="">Any status</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <label htmlFor="queue-assignment" className="text-sm font-medium">Assignment</label>
            <select id="queue-assignment" data-testid="queue-assignment" value={filters.assignment}
                    className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                    onChange={(e) => setFilters({ ...filters, assignment: e.target.value })}>
              <option value="">Everyone</option>
              <option value="mine">Assigned to me</option>
              <option value="unassigned">Unassigned</option>
            </select>
          </div>
        </section>

        {summary && (
          <section className="grid gap-3 md:grid-cols-3 text-sm" data-testid="queue-counts">
            <div className="border rounded-md px-3 py-2" data-testid="queue-count-open">
              <p className="text-xs text-muted-foreground">Open</p>
              <p className="text-2xl font-semibold">{summary.open_count}</p>
            </div>
            <div className="border rounded-md px-3 py-2" data-testid="queue-count-unassigned">
              <p className="text-xs text-muted-foreground">Unassigned and open</p>
              <p className="text-2xl font-semibold">{summary.unassigned_count}</p>
            </div>
            <div className="border rounded-md px-3 py-2" data-testid="queue-load">
              <p className="text-xs text-muted-foreground">Load by queue</p>
              <p className="text-sm">
                {Object.entries(summary.queue_load).map(([q, n]) => `${q}: ${n}`).join(" · ") || "—"}
              </p>
            </div>
          </section>
        )}

        {summary && !summary.can_triage && (
          <p className="text-sm text-amber-800" data-testid="queue-readonly">
            You can read this queue but not triage it — assignment and priority need requests.write.
          </p>
        )}

        <section className="space-y-2" data-testid="queue-table">
          <h2 className="text-base md:text-lg font-medium">Work in this queue</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border rounded-md">
              <caption className="sr-only">Requests owned by this provider</caption>
              <thead>
                <tr className="text-left border-b">
                  <th scope="col" className="px-3 py-2">Number</th>
                  <th scope="col" className="px-3 py-2">Title</th>
                  <th scope="col" className="px-3 py-2">Queue</th>
                  <th scope="col" className="px-3 py-2">Site</th>
                  <th scope="col" className="px-3 py-2">Status</th>
                  <th scope="col" className="px-3 py-2">Priority</th>
                  <th scope="col" className="px-3 py-2">Assignee</th>
                  <th scope="col" className="px-3 py-2">Open</th>
                </tr>
              </thead>
              <tbody>
                {(view || []).map((r) => (
                  <tr key={r.id} className="border-b last:border-0"
                      data-testid={`queue-row-${r.number}`}>
                    <th scope="row" className="px-3 py-2 font-mono text-left">{r.number}</th>
                    <td className="px-3 py-2">{r.title}</td>
                    <td className="px-3 py-2">{r.queue || "unrouted"}</td>
                    <td className="px-3 py-2">{r.location_key || "—"}</td>
                    <td className="px-3 py-2">{r.status_category}</td>
                    <td className="px-3 py-2">{r.priority}</td>
                    <td className="px-3 py-2">{r.assignee_email || "unassigned"}</td>
                    <td className="px-3 py-2">
                      <Button size="sm" variant="ghost" data-testid={`queue-open-${r.number}`}
                              onClick={() => openRequest(r.id)}>Triage</Button>
                    </td>
                  </tr>
                ))}
                {view && view.length === 0 && (
                  <tr><td colSpan="8" className="px-3 py-2 text-muted-foreground"
                          data-testid="queue-empty">
                    Nothing matches these filters.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {selected && (
          <section className="space-y-4 border-t pt-6" data-testid="queue-detail">
            <div className="flex items-center justify-between">
              <h2 className="text-base md:text-lg font-medium" data-testid="queue-detail-title">
                {selected.number} — {selected.title}
              </h2>
              <Button size="sm" variant="ghost" data-testid="queue-detail-close"
                      onClick={() => setSelected(null)}>Close</Button>
            </div>
            <p className="text-sm" data-testid="queue-detail-meta">
              {selected.state} · {selected.status_category} · priority {selected.priority} ·
              site {selected.location_key || "none"} · queue {selected.queue || "unrouted"} ·
              assignee {selected.assignee_email || "unassigned"}
            </p>
            {selected.masked_fields?.length > 0 && (
              <p className="text-sm text-amber-800" data-testid="queue-detail-masked">
                {selected.masked_fields.length} restricted field(s) are withheld from you.
              </p>
            )}

            <div className="grid gap-4 md:grid-cols-2">
              <div className="border rounded-md p-4 space-y-2">
                <h3 className="text-sm font-semibold">Assignment</h3>
                <label htmlFor="queue-assignee" className="text-sm">Assign to</label>
                <select id="queue-assignee" data-testid="queue-assignee-select"
                        className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                        value={selected.assignee_email || ""}
                        onChange={(e) => post(`/requests/${selected.id}/assign`,
                                               { assignee_email: e.target.value || null },
                                               "Assignment")}>
                  <option value="">Unassigned</option>
                  {members.map((m) => (
                    <option key={m.email} value={m.email}>{m.name} ({m.email})</option>
                  ))}
                </select>

                <h3 className="text-sm font-semibold pt-2">Priority</h3>
                <select aria-label="Priority" data-testid="queue-priority-select"
                        className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                        value={draft.priority}
                        onChange={(e) => setDraft({ ...draft, priority: e.target.value })}>
                  {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
                <Input placeholder="Reason for the override" value={draft.reason}
                       data-testid="queue-priority-reason"
                       onChange={(e) => setDraft({ ...draft, reason: e.target.value })} />
                <Button size="sm" variant="outline" data-testid="queue-priority-save"
                        onClick={() => post(`/requests/${selected.id}/priority`,
                                             { priority: draft.priority, reason: draft.reason },
                                             "Priority change")}>
                  Change priority
                </Button>
              </div>

              <div className="border rounded-md p-4 space-y-2">
                <h3 className="text-sm font-semibold">Move it on</h3>
                <div className="flex flex-wrap gap-2" data-testid="queue-transitions">
                  {((view || []).find((r) => r.id === selected.id)?.allowed_transitions || [])
                    .map((s) => (
                      <Button key={s} size="sm" variant="outline"
                              data-testid={`queue-transition-${s}`}
                              onClick={() => post(`/requests/${selected.id}/transition`,
                                                   { to_state: s }, `Moved to ${s}`)}>
                        {s}
                      </Button>
                    ))}
                </div>
                <h3 className="text-sm font-semibold pt-2">Transfer to another provider</h3>
                <select aria-label="Transfer to provider" data-testid="queue-transfer-select"
                        className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                        value={draft.transfer}
                        onChange={(e) => setDraft({ ...draft, transfer: e.target.value })}>
                  <option value="">Choose a provider…</option>
                  {providers.filter((p) => p.key !== selected.provider_key).map((p) => (
                    <option key={p.key} value={p.key}>{p.name}</option>
                  ))}
                </select>
                <Input placeholder="Why it is moving" value={draft.transferReason}
                       data-testid="queue-transfer-reason"
                       onChange={(e) => setDraft({ ...draft, transferReason: e.target.value })} />
                <Button size="sm" variant="outline" data-testid="queue-transfer-save"
                        onClick={() => post(`/requests/${selected.id}/transfer`,
                                             { to_provider: draft.transfer,
                                               reason: draft.transferReason }, "Transfer")}>
                  Transfer
                </Button>
              </div>
            </div>

            <div className="border rounded-md p-4 space-y-2">
              <h3 className="text-sm font-semibold">Notes</h3>
              <ul className="text-sm space-y-1" data-testid="queue-comments">
                {comments.map((c) => (
                  <li key={c.id} data-testid={`queue-comment-${c.id}`}>
                    <span className="text-muted-foreground">
                      {c.internal ? "internal" : "visible to requester"}:
                    </span>{" "}
                    {c.body}
                  </li>
                ))}
                {comments.length === 0 && (
                  <li className="text-muted-foreground" data-testid="queue-comments-empty">
                    No notes yet.
                  </li>
                )}
              </ul>
              <Input placeholder="Add a note" value={draft.comment} data-testid="queue-comment-input"
                     onChange={(e) => setDraft({ ...draft, comment: e.target.value })} />
              <div className="flex items-center gap-2">
                <input id="queue-comment-internal" type="checkbox" checked={draft.internal}
                       data-testid="queue-comment-internal"
                       onChange={(e) => setDraft({ ...draft, internal: e.target.checked })} />
                <label htmlFor="queue-comment-internal" className="text-sm">
                  Internal only (not shown to the requester)
                </label>
                <Button size="sm" variant="outline" data-testid="queue-comment-add"
                        onClick={() => post("/comments", { parent_type: "work_request",
                                                           parent_id: selected.id,
                                                           body: draft.comment,
                                                           internal: draft.internal }, "Note")}>
                  Add note
                </Button>
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
